import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

interface HorizontalScrollState {
  canScrollLeft: boolean;
  canScrollRight: boolean;
}

// Sub-pixel rounding can leave a fully-scrolled element a pixel short of its
// limit; treat anything within this band as "at the edge".
const EDGE_EPSILON = 1;

/**
 * Tracks whether a horizontally scrollable element has content hidden past
 * either edge, so callers can show fade/advance affordances only when there's
 * somewhere to scroll. Updated on scroll, resize, and size changes to the
 * element and its children (label wrap / late fonts change scrollWidth alone).
 */
export function useHorizontalScrollState(
  scrollRef: RefObject<HTMLElement | null>,
): HorizontalScrollState {
  const [scrollState, setScrollState] = useState<HorizontalScrollState>({
    canScrollLeft: false,
    canScrollRight: false,
  });

  useEffect(() => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    const syncScrollState = () => {
      const maxScrollLeft = element.scrollWidth - element.clientWidth;
      const next = {
        canScrollLeft: element.scrollLeft > EDGE_EPSILON,
        canScrollRight: element.scrollLeft < maxScrollLeft - EDGE_EPSILON,
      };

      setScrollState((current) =>
        current.canScrollLeft === next.canScrollLeft &&
        current.canScrollRight === next.canScrollRight
          ? current
          : next,
      );
    };

    syncScrollState();

    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(syncScrollState)
        : null;

    if (resizeObserver) {
      resizeObserver.observe(element);

      for (const child of element.children) {
        resizeObserver.observe(child);
      }
    }

    element.addEventListener('scroll', syncScrollState, { passive: true });
    window.addEventListener('resize', syncScrollState);

    return () => {
      resizeObserver?.disconnect();
      element.removeEventListener('scroll', syncScrollState);
      window.removeEventListener('resize', syncScrollState);
    };
  }, [scrollRef]);

  return scrollState;
}
